import axios from "axios";
import { CONVERSATION_PAGE_SIZE } from "../constants";
import { updateParticipants } from "../../../../store/twilio-reducers/chatSlice";
import { SMS_PREFIX } from "../constants";
import { Client } from "@twilio/conversations";

export async function addConversation(name, updateParticipants, client) {
  let conversation;
  if (client === undefined) {
    throw new Error(
      "Client is suddenly undefined, are you sure everything is ok?"
    );
  }

  if (name.length === 0) {
    throw new Error("Conversation name is empty");
  }

  try {
    conversation = await client.createConversation({
      friendlyName: name,
    });
    await conversation.join();

    const participants = await conversation.getParticipants();
    updateParticipants(participants, conversation.sid);

    return conversation;
  } catch (e) {
    throw e;
  }
}

export async function addNonChatParticipant(
  address,
  proxyNumber,
  client,
  leadInfo
) {
  let conversation;
  try {
    conversation = await addConversation(address, updateParticipants, client);

    if (conversation === undefined) {
      throw new Error(
        "Conversation is suddenly undefined, are you sure everything is ok?"
      );
    }

    if (address.length === 0 || proxyNumber.length === 0) {
      throw new Error(
        "Both participant number and proxy number must be specified"
      );
    }
    const simplifiedLeadInfo = {
      first_name: leadInfo.first_name,
      last_name: leadInfo.last_name,
      email: leadInfo.email,
      phone_number: leadInfo.phone_number,
      city: leadInfo.city,
      // state: leadInfo.state,
      postal_code: leadInfo.postal_code,
      id: leadInfo.id
    };

    const sanitizedLeadInfo = Object.fromEntries(
      Object.entries(simplifiedLeadInfo).filter(
        ([key, value]) => value !== null
      )
    );
    const result = await conversation.addNonChatParticipant(
      proxyNumber,
      address,
      {
        friendlyName: address,
        ...sanitizedLeadInfo,
      }
    );
    return result;
  } catch (e) {
    throw e;
  }
}

export async function getToken(username, password) {
  const requestAddress = process.env.REACT_APP_ACCESS_TOKEN_SERVICE_URL;
  if (!requestAddress) {
    throw new Error(
      "REACT_APP_ACCESS_TOKEN_SERVICE_URL is not configured, cannot login"
    );
  }

  try {
    const response = await axios.get(requestAddress, {
      params: { identity: username, password: password },
    });
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 401) {
      throw new Error(error.response.data ?? "Authentication error.");
    }

    console.error(`ERROR received from ${requestAddress}: ${error}\n`);
    throw new Error(`ERROR received from ${requestAddress}: ${error}\n`);
  }
}

export const getFileUrl = async (media) => {
  return await media.getContentTemporaryUrl();
};

export const getMessages = async (conversation) => {
  return await conversation.getMessages(CONVERSATION_PAGE_SIZE);
};

const replaceTemplatePlaceholders = (template, data) => {
  // Define the replacement values for each placeholder
  const replacements = {
    "{{match_first_name}}": data?.lead?.first_name || "",
    "{{match_last_name}}": data?.lead?.last_name || "",
    "{{match_full_name}}": `${data?.user?.first_name || ""} ${
      data?.user?.last_name || ""
    }`.trim(),
    "{{my_name}}": data?.user?.first_name || "",
    "{{my_profession}}": data?.profession?.name || "",
    "{{office_phone}}": data?.user?.office_phone_number || "",
    "{{schedule_link}}": data?.user?.schedule_url || "",
    "{{ehr_sms}}": data?.user?.ehr_sms || "",
  };

  // Replace each placeholder with the corresponding value
  return Object.keys(replacements).reduce((result, key) => {
    return result.replace(new RegExp(key, "g"), replacements[key]);
  }, template);
};

export const findMatchedParticipant = async (client,data) => {
  const leadPhoneNumber = `+1${data?.lead?.phone_number}`;
  try {
    const conversations = await client.getSubscribedConversations();
    const matched = conversations.items.find(
      (con) => con.friendlyName == leadPhoneNumber
    );
    return matched;
  } catch (err) {
    console.error("Error finding conversation:", err);
  }
};

export  const handleTwilioConversation = async (twilioClientInstance, dataToUse) => {
  const { lead, user } = dataToUse || {};
  const leadPhoneNumber = `1${lead?.phone_number}`;
  const twilioPhoneNumber = user?.twilio_phone_number?.replace("+", "");
  const initialSmsTemplate = user?.message_templates?.find(
    (template) => template.type === "initial_sms"
  )?.content;

  if (!initialSmsTemplate) {
    console.error("Initial SMS template not found.");
    return;
  }

  try {
    // Attempt to find an existing participant
    const matchedParticipant = await findMatchedParticipant(twilioClientInstance ,dataToUse);
    const messageContent = replaceTemplatePlaceholders(initialSmsTemplate, dataToUse);
    if (matchedParticipant) {
      await matchedParticipant.sendMessage(messageContent);
      console.log("Initial message sent successfully without creating conversation.");
      return;
    }

    // Create a new conversation if no participant matches
    const result = await addNonChatParticipant(
      `${SMS_PREFIX}${leadPhoneNumber}`,
      `${SMS_PREFIX}${twilioPhoneNumber}`,
      twilioClientInstance,
      lead
    );

    const conversationSid = result?.conversation_sid;
    if (!conversationSid) {
      console.error("Conversation SID is undefined. Check addNonChatParticipant response.");
      return;
    }

    const conversation = await twilioClientInstance.getConversationBySid(conversationSid);
    await conversation.sendMessage(messageContent);
      localStorage.setItem('messageSentKey', true);

    console.log("Initial message sent successfully.");
  } catch (error) {
    console.error("Error handling Twilio conversation:", error.message || error);
  }
};

export const createTwilioClient = async (token , dataToUse) => {
  try {
    if (token) {
      const clientInstance = new Client(token);
      clientInstance.on("connectionStateChanged", (state) => {
        if (state === "connected") {
          handleTwilioConversation(clientInstance , dataToUse); // Call handleTwilioConversation when connected
        }
      });
      return clientInstance;
    }
  } catch (error) {
    return {error:"Error fetching Twilio token"}
    // toast.error("Error fetching Twilio token");
  }
};
